import { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useSearchParams, useRouter } from '@/routing/navigation'
import Link from '@/routing/Link'
import { ArrowRight, Check, Loader2, X } from '@digital-twins/design-system/icons'
import { authApi } from '@/lib/api'
import { useAuth } from '@/lib/config/auth-provider'
import { getReferralCookies, clearReferralCookies } from '@/lib/utils/referral-cookie'
import { getApiErrorMessage } from '@/lib/get-api-error-message'
import { TosAgreeCopy } from '@/components/auth/TosAgreeCopy'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Heading } from '@/components/ui/Heading'
import { Text } from '@/components/ui/Text'

export default function RegisterSsoCompletePage() {
  const { t } = useTranslation(['auth', 'common'])
  const searchParams = useSearchParams()
  const router = useRouter()
  const { loginWithResponse } = useAuth()

  const token = searchParams.get('token') || ''
  const needsEmail = searchParams.get('needsEmail') === '1'
  const { referralCode: referralCookieCode } = getReferralCookies()
  const [referralValidated, setReferralValidated] = useState<boolean | null>(
    referralCookieCode ? null : false,
  )
  const [referralValidating, setReferralValidating] = useState(!!referralCookieCode)
  const isAccessCodeExempt =
    !!referralCookieCode && referralValidated === true

  const [tosAccepted, setTosAccepted] = useState(false)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')

  const [email, setEmail] = useState('')

  const [accessCode, setAccessCode] = useState('')
  const [codeValidated, setCodeValidated] = useState<boolean | null>(null)
  const [codeValidating, setCodeValidating] = useState(false)
  const [codeError, setCodeError] = useState('')

  useEffect(() => {
    if (!token) {
      router.replace('/login?error=sso_registration_expired')
    }
  }, [token, router])

  useEffect(() => {
    if (!referralCookieCode) {
      return
    }
    setReferralValidating(true)
    authApi.validateReferralCode(referralCookieCode)
      .then(({ valid }) => {
        setReferralValidated(valid)
      })
      .catch(() => {
        setReferralValidated(false)
      })
      .finally(() => {
        setReferralValidating(false)
      })
  }, [referralCookieCode])

  if (!token) return null

  async function validateCode(code: string): Promise<boolean | null> {
    if (!code.trim()) {
      setCodeValidated(null)
      setCodeError('')
      return null
    }
    setCodeValidating(true)
    setCodeError('')
    try {
      const { valid } = await authApi.validateAccessCode(code.trim())
      setCodeValidated(valid)
      if (!valid) setCodeError(t('complete.errors.invalidCode'))
      return valid
    } catch {
      setCodeValidated(false)
      setCodeError(t('complete.errors.verifyFailed'))
      return false
    } finally {
      setCodeValidating(false)
    }
  }

  function handleCodeBlur() {
    if (accessCode.trim()) {
      validateCode(accessCode)
    } else {
      setCodeValidated(null)
      setCodeError('')
    }
  }

  function handleCodeChange(value: string) {
    setAccessCode(value)
    if (codeValidated !== null) {
      setCodeValidated(null)
      setCodeError('')
    }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setLoading(true)
    setError('')

    try {
      const trimmedAccessCode = accessCode.trim()
      let shouldApplyPromoCode = codeValidated === true

      if (trimmedAccessCode && !shouldApplyPromoCode) {
        const isCodeValid = await validateCode(trimmedAccessCode)
        if (!isCodeValid) {
          return
        }
        shouldApplyPromoCode = true
      }

      const { referralCode, referralSource } = getReferralCookies()
      const response = await authApi.completeOauthRegistration({
        token,
        tosAcceptedAt: new Date().toISOString(),
        email: needsEmail ? email.trim() : undefined,
        promoCode: shouldApplyPromoCode ? trimmedAccessCode : undefined,
        referralCode:
          referralValidated === true && referralCode ? referralCode : undefined,
        referralSource:
          referralValidated === true && referralSource
            ? referralSource
            : undefined,
      })

      clearReferralCookies()

      if (response.pendingVerification) {
        const verifyParams = new URLSearchParams({ email: response.email })
        if (response.verificationEmailSent === false) verifyParams.set('emailNotSent', '1')
        router.push(`/register/verify?${verifyParams.toString()}`)
        return
      }

      loginWithResponse({ ...response, isNewUser: true })
      router.push('/')
    } catch (err: unknown) {
      const msg = getApiErrorMessage(err, t('complete.errors.registrationFailed'))
      // Expired / invalid token — send user back to login with a recovery hint.
      if (msg.toLowerCase().includes('expired') || msg.toLowerCase().includes('invalid registration')) {
        router.replace('/login?error=sso_registration_expired')
        return
      }
      setError(msg)
    } finally {
      setLoading(false)
    }
  }

  const isEmailValid = !needsEmail || (email.trim().length > 0 && email.includes('@'))
  const codeReady = isAccessCodeExempt || codeValidated === true
  const canSubmit =
    tosAccepted &&
    !loading &&
    !codeValidating &&
    !referralValidating &&
    isEmailValid &&
    codeReady

  return (
    <div className="flex w-full flex-col gap-6 animate-in fade-in slide-in-from-bottom-4 duration-500">
      <div className="flex flex-col gap-1">
        <Heading level={1} size="3xl" className="text-center font-display font-normal tracking-tight">
          {t('complete.title')}
        </Heading>
        <Text as="p" size="sm" color="muted" className="text-center">
          {t('complete.ssoSubtitle')}
        </Text>
      </div>

      <form onSubmit={handleSubmit} className="flex flex-col gap-4">
        {needsEmail && (
          <div className="space-y-2">
            <Label htmlFor="sso-email">{t('complete.emailAddress')}</Label>
            <Input
              id="sso-email"
              data-testid="sso-complete-email-input"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder={t('complete.emailPlaceholder')}
              autoComplete="email"
              required
            />
            <Text as="p" size="xs" color="muted">
              {t('complete.microsoftEmailHint')}
            </Text>
          </div>
        )}

        {!isAccessCodeExempt && (
        <div className="space-y-2">
          <Label htmlFor="access-code">{t('complete.accessCode')}</Label>
          <div className="relative">
            <Input
              id="access-code"
              data-testid="sso-complete-access-code-input"
              type="text"
              value={accessCode}
              onChange={(e) => handleCodeChange(e.target.value)}
              onBlur={handleCodeBlur}
              placeholder={t('complete.ssoAccessCodePlaceholder')}
              autoComplete="off"
              className={
                codeValidated === false
                  ? 'border-destructive pr-9'
                  : codeValidated === true
                  ? 'border-emerald-500 pr-9'
                  : 'pr-9'
              }
            />
            <div className="absolute right-3 top-1/2 -translate-y-1/2">
              {codeValidating && (
                <Loader2 className="size-4 animate-spin text-muted-foreground" />
              )}
              {!codeValidating && codeValidated === true && (
                <Check className="size-4 text-emerald-500" />
              )}
              {!codeValidating && codeValidated === false && (
                <X className="size-4 text-destructive" />
              )}
            </div>
          </div>
          {codeError && (
            <Text
              as="p"
              size="xs"
              data-testid="sso-complete-access-code-error"
              className="text-destructive"
            >
              {codeError}
            </Text>
          )}
        </div>
        )}

        <div className="flex items-start gap-3">
          <input
            type="checkbox"
            id="tos"
            data-testid="register-tos-checkbox"
            checked={tosAccepted}
            onChange={(e) => setTosAccepted(e.target.checked)}
            className="mt-1 h-4 w-4 rounded border-border text-primary focus:ring-primary"
          />
          <Label
            htmlFor="tos"
            className="text-sm font-normal leading-relaxed text-muted-foreground cursor-pointer"
          >
            <TosAgreeCopy />
          </Label>
        </div>

        {error && (
          <div
            data-testid="sso-complete-error"
            className="rounded-lg border border-destructive/50 bg-destructive/10 p-3 text-sm text-destructive"
          >
            {error}
          </div>
        )}

        <Button
          type="submit"
          variant="cta"
          disabled={!canSubmit}
          data-testid="sso-complete-submit-button"
          className="w-full gap-2"
        >
          {loading ? (
            <>
              <Loader2 className="mr-2 size-4 animate-spin" />
              {t('complete.submitting')}
            </>
          ) : (
            <>
              {t('complete.submit')}
              <ArrowRight className="size-4" />
            </>
          )}
        </Button>
      </form>

      <Text as="p" size="sm" color="muted" className="text-center">
        <Button variant="link" size="sm" className="h-auto p-0 text-sm" asChild>
          <Link href="/login">{t('backToSignIn')}</Link>
        </Button>
      </Text>
    </div>
  )
}
